import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Button, Icon, Text as ChakraText } from '@chakra-ui/react'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AiOutlineUserDelete } from 'react-icons/ai'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import type { UserErrorResponse, UserSessionResponse } from '../../interfaces'

interface Props {
  userName?: string
  userEmail?: String
  roleDescription?: string
  id?: string
  user: UserSessionResponse | null
  isOpen: boolean
  onClose(): void
  getData(): Promise<void>
}

const DeleteUserModal = ({
  userName,
  userEmail,
  roleDescription,
  id,
  user,
  isOpen,
  onClose,
  getData,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const toast = useToast()

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Icon
            as={AiOutlineUserDelete}
            color={colors.appBattleshipGrey}
            backgroundColor={colors.appPaleGrey}
            boxSize={6}
            padding="0.1em"
            borderRadius="50%"
            marginRight="0.5em"
          />
          {t('delete_user', 'Delete User')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Trans t={t} i18nKey="delete_user_modal_description">
            <ChakraText color={colors.appBattleshipGrey}>
              This will revoke access of user {{ userName }} with email ID{' '}
              {{ userEmail }} having role of {{ roleDescription }}. Once
              submitted, this action cannot be reversed.
            </ChakraText>
          </Trans>
        </ModalBody>
        <ModalFooter
          borderTop={`1px solid ${colors.appGrey}`}
          style={{ gap: '1em' }}
        >
          <Button onClick={onClose} variant="outline">
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            colorScheme="appRed"
            type="submit"
            onClick={async () => {
              const response = (await request.delete(
                `/api/clients/${user?.clientNumber}/users/${id}`,
              )) as null | UserErrorResponse

              if (request.error) {
                toast({
                  description: response?.message,
                  status: 'error',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
              } else {
                toast({
                  description: t(
                    'successfully_deleted',
                    'Successfully deleted',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })

                await getData()
              }

              onClose()
            }}
          >
            {t('submit', 'Submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default DeleteUserModal
